// Tool factories for the per-chat canvas surface. The agent uses these
// to pin / update / remove renders on the canvas the user has opted
// into. State lives in a module-level store (lib/canvasStore.js) so
// each tool can read + write synchronously — React state would be too
// late: setState is batched, and the tool needs to return the new
// item's id and see its own writes on a subsequent call.
//
// Why a tool family instead of one omnibus tool: each operation has a
// distinct shape and intent. Letting the LLM call `canvas_inspect`
// before deciding whether to add or update is the whole point — the
// canvas is a stateful surface the agent reasons about, not a
// fire-and-forget render pipe.

import { Type } from "@earendil-works/pi-ai";
import { KNOWN_RENDER_KINDS } from "./kernelPrimitiveSpecs.js";
import { coerceJsonArg } from "./coerceJsonArg.js";
import {
    readCanvas,
    addCanvasItem,
    updateCanvasItem,
    removeCanvasItem,
    clearCanvasItems,
    summariseCanvas,
} from "./canvasStore.js";

// Detect the common agent mistake of passing a bare identifier string
// like "result" or "rows" as `data`, expecting it to resolve to a
// prior step's output. This tool has no ref resolution; treat short
// identifier-shaped strings as the mistake they almost always are and
// reject with a clear retry hint.
const STEP_REF_LIKE = /^[a-z][a-z0-9_]{0,30}$/i;
function looksLikeStepRef(data) {
    return typeof data === "string" && !!data && STEP_REF_LIKE.test(data);
}

// Validate a render spec at the tool boundary. Returns null when the
// spec is fine, or { message, details } describing the problem so the
// caller can return it verbatim as a tool error. Used by both
// canvas_add and canvas_update so the agent gets identical feedback
// whether it's pinning fresh or editing.
function validateSpec(spec) {
    if (!spec || typeof spec !== "object" || Array.isArray(spec)) {
        return {
            message:
                "spec must be a JSON object describing a render primitive (e.g. `{kind: \"chart\", series: [...], ...}`). Same shape as render_block — see its description for valid shapes.",
            details: { problem: "not an object", got: spec === null ? "null" : Array.isArray(spec) ? "array" : typeof spec },
        };
    }
    if (typeof spec.kind !== "string" || !spec.kind) {
        return {
            message:
                "spec.kind is required and must be a string naming the render primitive (e.g. \"chart\", \"heatmap\", \"treemap\"). See render_block's spec doc for the full set.",
            details: { problem: "missing kind", got: typeof spec.kind },
        };
    }
    if (!KNOWN_RENDER_KINDS.has(spec.kind)) {
        return {
            message:
                `unknown render kind "${spec.kind}". Valid kinds: ${[...KNOWN_RENDER_KINDS].join(", ")}. Pick the kind whose schema matches your data, then call canvas_add again.`,
            details: { problem: "unknown kind", got: spec.kind, valid: [...KNOWN_RENDER_KINDS] },
        };
    }
    return null;
}

const SHARED_NOTE =
    "The canvas is a persistent surface alongside the chat that holds renders the user wants kept in view across turns. It is OPT-IN: pin only when the user has asked you to or when you're building an evolving multi-view summary they've asked you to maintain. Most renders should still go inline via `render_block`. Captions are curated commentary the user reads — write clear, short ones.";

export function createCanvasAddTool({ chatId }) {
    return {
        name: "canvas_add",
        label: "Pin to canvas",
        description:
            "Pin a render to the chat's canvas surface. The render is shown in the right-hand canvas column and persists until removed.\n\n" +
            SHARED_NOTE +
            "\n\n" +
            "Before calling, run `canvas_inspect()` to see what's already pinned. If a similar item exists, prefer `canvas_update(id, …)` instead of adding a duplicate.\n\n" +
            // The chat offers canvas tools only beside render_block, whose
            // description carries the full spec doc; one copy is enough.
            "`spec` and `data` use the same shape as `render_block`; its description lists the valid kinds and each kind's fields.",
        parameters: Type.Object({
            // Typed object (not Type.Unknown) to steer the model toward an
            // object literal instead of a JSON-encoded string — see the
            // matching note in render_block. coerceJsonArg in execute() is
            // still the safety net.
            spec: Type.Object({
                kind: Type.String({
                    description:
                        "The render primitive to use (e.g. \"chart\", \"bars\", \"treemap\"). Must be one of the kinds listed in render_block's description.",
                }),
            }, {
                additionalProperties: true,
                description: "Render spec — a JSON object (NOT a JSON-encoded string), must include `kind`. Same shape as render_block.",
            }),
            data: Type.Unknown({
                description:
                    "Computed values for this render — INLINE JSON (numbers, arrays, objects). Do not pass step-reference names. Same shape rules as render_block.",
            }),
            caption: Type.Optional(
                Type.String({
                    description:
                        "Short caption shown above the render on the canvas (≤140 chars). Explains what the item is or how it relates to other pinned items.",
                }),
            ),
            sourceToolCallIds: Type.Optional(
                Type.Array(Type.String(), {
                    description:
                        "STRONGLY RECOMMENDED. List of `_tool_call_id` strings from the prior analytics_query / execute_python calls whose output produced this item's `data`. Find each id in the `_tool_call_id` field echoed at the top of the prior tool result — copy verbatim. These hints make later crystallising this canvas item into a saved kernel deterministic (the save flow can lift the exact chain); without them, the save flow has to scan the entire transcript heuristically and is far more failure-prone. Include only the SUCCESSFUL tool calls; omit failed retries.",
                }),
            ),
        }),
        async execute(_id, args) {
            // Some model families double-encode object/array params as a
            // JSON string (spec arrives as "{\"kind\":...}" rather than an
            // object). Decode before validating so a stringified spec isn't
            // rejected on one model but accepted on another.
            const spec = coerceJsonArg(args.spec);
            const data = coerceJsonArg(args.data);
            const { caption, sourceToolCallIds } = args;
            // Validate BEFORE mutating. Rejecting the call here means
            // the canvas stays clean and the agent sees a detailed
            // retryable error — we do NOT add a placeholder broken
            // item just so the user can "see" the attempt landed.
            const specError = validateSpec(spec);
            if (specError) {
                return {
                    content: [{ type: "text", text: `canvas_add: ${specError.message}` }],
                    details: { error: specError.message, ...specError.details },
                    isError: true,
                };
            }
            if (looksLikeStepRef(data)) {
                return {
                    content: [{
                        type: "text",
                        text: `canvas_add: data is "${data}" — that looks like a step reference name, but this tool has no ref resolution. Pass the actual computed values inline (numbers / arrays / objects), same as render_block. If the values came from a prior analytics_query, inline the row array; if from execute_python, return the value from python and inline that here.`,
                    }],
                    details: { error: "data appears to be a step reference", got: data },
                    isError: true,
                };
            }
            const { addedId } = addCanvasItem(chatId, { spec, data, caption, sourceToolCallIds });
            const provNote = Array.isArray(sourceToolCallIds) && sourceToolCallIds.length
                ? ` (provenance: ${sourceToolCallIds.length} tool call${sourceToolCallIds.length === 1 ? "" : "s"})`
                : " — no sourceToolCallIds tagged; later kernel save will have to scan the transcript heuristically. Consider canvas_update with the ids of the analytics_query / execute_python calls that produced this data.";
            return {
                content: [{
                    type: "text",
                    text: `pinned ${spec.kind} to canvas (id=${addedId})${caption ? ` — "${caption}"` : ""}${provNote}`,
                }],
                details: {
                    kind: "canvasMutation",
                    op: "add",
                    id: addedId,
                    kind_: spec.kind,
                    caption: caption || null,
                    sourceToolCallIds: Array.isArray(sourceToolCallIds) ? sourceToolCallIds : null,
                },
            };
        },
    };
}

export function createCanvasUpdateTool({ chatId }) {
    return {
        name: "canvas_update",
        label: "Update canvas item",
        description:
            "Replace one or more fields on a previously-pinned canvas item. Use when refreshing data (new time range, updated query), swapping the render kind for a better fit, or rewriting the caption. Pass only the fields you want to change — omitted fields stay as-is.\n\n" +
            "If you don't know the id, call `canvas_inspect()` first.\n\n" +
            SHARED_NOTE,
        parameters: Type.Object({
            id: Type.String({
                description: "Id of the canvas item to update (returned by canvas_add, or visible in canvas_inspect).",
            }),
            spec: Type.Optional(Type.Object({
                kind: Type.String({
                    description: "The render primitive to use. Full replacement — include `kind` and all per-kind fields.",
                }),
            }, {
                additionalProperties: true,
                description: "New render spec — a JSON object (NOT a JSON-encoded string). Full replacement — pass the complete spec, not a partial.",
            })),
            data: Type.Optional(Type.Unknown({
                description: "New computed values. Same shape as render_block's data param.",
            })),
            caption: Type.Optional(Type.String({
                description: "New caption. Pass an empty string to clear the existing caption.",
            })),
            sourceToolCallIds: Type.Optional(Type.Array(Type.String(), {
                description:
                    "Replace this item's provenance hint — list of `_tool_call_id`s for the analytics_query / execute_python calls that produced its `data`. Pass an empty array to clear. Set this if you forgot to include it at canvas_add time, or if the recipe behind the item changed.",
            })),
        }),
        async execute(_id, args) {
            const { id, caption, sourceToolCallIds } = args;
            // Decode double-encoded JSON-string params (see canvas_add).
            const spec = coerceJsonArg(args.spec);
            const data = coerceJsonArg(args.data);
            if (!id || typeof id !== "string") {
                return {
                    content: [{ type: "text", text: "canvas_update: id is required" }],
                    details: { error: "missing id" },
                    isError: true,
                };
            }
            const partial = {};
            if (spec !== undefined) {
                const specError = validateSpec(spec);
                if (specError) {
                    return {
                        content: [{ type: "text", text: `canvas_update: ${specError.message}` }],
                        details: { error: specError.message, ...specError.details },
                        isError: true,
                    };
                }
                partial.spec = spec;
            }
            if (data !== undefined) {
                if (looksLikeStepRef(data)) {
                    return {
                        content: [{
                            type: "text",
                            text: `canvas_update: data is "${data}" — that looks like a step reference name. Pass the actual computed values inline (numbers / arrays / objects), same as render_block.`,
                        }],
                        details: { error: "data appears to be a step reference", got: data },
                        isError: true,
                    };
                }
                partial.data = data;
            }
            if (caption !== undefined) partial.caption = caption;
            if (sourceToolCallIds !== undefined) partial.sourceToolCallIds = sourceToolCallIds;

            const { found } = updateCanvasItem(chatId, id, partial);
            if (!found) {
                return {
                    content: [{ type: "text", text: `canvas_update: no item with id=${id} (call canvas_inspect to see live ids)` }],
                    details: { error: "id not found", id },
                    isError: true,
                };
            }
            const changed = Object.keys(partial);
            return {
                content: [{
                    type: "text",
                    text: `updated canvas item ${id} · changed: ${changed.length ? changed.join(", ") : "nothing"}`,
                }],
                details: { kind: "canvasMutation", op: "update", id, changed },
            };
        },
    };
}

export function createCanvasRemoveTool({ chatId }) {
    return {
        name: "canvas_remove",
        label: "Remove canvas item",
        description:
            "Remove a single pinned item from the canvas. Use when the user says \"drop the heatmap\", \"unpin that\", or when an item has been superseded by an updated one.\n\n" +
            "If you don't know the id, call `canvas_inspect()` first.\n\n" +
            SHARED_NOTE,
        parameters: Type.Object({
            id: Type.String({
                description: "Id of the canvas item to remove.",
            }),
        }),
        async execute(_id, { id }) {
            if (!id || typeof id !== "string") {
                return {
                    content: [{ type: "text", text: "canvas_remove: id is required" }],
                    details: { error: "missing id" },
                    isError: true,
                };
            }
            const { found } = removeCanvasItem(chatId, id);
            if (!found) {
                return {
                    content: [{ type: "text", text: `canvas_remove: no item with id=${id}` }],
                    details: { error: "id not found", id },
                    isError: true,
                };
            }
            return {
                content: [{ type: "text", text: `removed canvas item ${id}` }],
                details: { kind: "canvasMutation", op: "remove", id },
            };
        },
    };
}

export function createCanvasClearTool({ chatId }) {
    return {
        name: "canvas_clear",
        label: "Clear canvas",
        description:
            "Remove ALL items from the canvas. Use only when the user explicitly asks to wipe it (\"clear the canvas\", \"start fresh\"). Don't call speculatively — pinned items are the user's working surface.\n\n" +
            SHARED_NOTE,
        parameters: Type.Object({}),
        async execute() {
            clearCanvasItems(chatId);
            return {
                content: [{ type: "text", text: "cleared canvas" }],
                details: { kind: "canvasMutation", op: "clear" },
            };
        },
    };
}

export function createCanvasInspectTool({ chatId }) {
    return {
        name: "canvas_inspect",
        label: "Inspect canvas",
        description:
            "List the items currently pinned to the canvas — id, kind, caption (or title). Cheap; call this freely before adding/updating so you can decide whether to refresh an existing item or pin a new one. Does NOT return spec/data bodies — call canvas_update or just re-render if you need full content.\n\n" +
            SHARED_NOTE,
        parameters: Type.Object({}),
        async execute() {
            const canvas = readCanvas(chatId);
            return {
                content: [{ type: "text", text: summariseCanvas(canvas) }],
                details: {
                    kind: "canvasInspection",
                    items: canvas.items.map((it) => ({
                        id: it.id,
                        kind: it.spec?.kind || null,
                        caption: it.caption || null,
                        title: it.spec?.title || it.spec?.label || null,
                        sourceToolCallIds: Array.isArray(it.sourceToolCallIds) ? it.sourceToolCallIds : null,
                        // Surface saved-kernel state so the agent can
                        // answer "is this saved?" / "which kernels did
                        // we crystallise?" without re-walking the
                        // chat trail. The user crystallises items
                        // outside the chat thread (no chat items
                        // emitted), so this field is the agent's only
                        // visibility into save state.
                        savedKernel: it.savedKernel || null,
                    })),
                },
            };
        },
    };
}

// Convenience: build all five at once. Used by createChatAgent so the
// wiring is a single line.
export function createCanvasTools({ chatId }) {
    return [
        createCanvasInspectTool({ chatId }),
        createCanvasAddTool({ chatId }),
        createCanvasUpdateTool({ chatId }),
        createCanvasRemoveTool({ chatId }),
        createCanvasClearTool({ chatId }),
    ];
}
